import { useRouterState } from '@tanstack/react-router'
import {
  AnimatePresence,
  type HTMLMotionProps,
  LazyMotion,
  MotionConfig,
  m,
  useReducedMotion,
  type Variants,
} from 'motion/react'
import type * as React from 'react'
import { useLayoutEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { DURATION, EASE_OUT, RISE, STAGGER, TRANSITION } from './tokens'

/** Las funciones de animación (incluidas las de layout) se cargan aparte: no pesan en la carga inicial. */
const loadFeatures = () => import('./features').then((mod) => mod.default)

/**
 * Configuración global: respeta `prefers-reduced-motion` del sistema (sin desplazamientos ni escalas) y usa la
 * curva de la app como transición por defecto. Va una sola vez en la raíz. Con `LazyMotion strict` toda la app
 * usa `m.*` (no `motion.*`), así el motor completo no entra en el paquete inicial.
 */
export function MotionProvider({ children }: { children: React.ReactNode }) {
  return (
    <LazyMotion features={loadFeatures} strict>
      <MotionConfig reducedMotion="user" transition={TRANSITION.base}>
        {children}
      </MotionConfig>
    </LazyMotion>
  )
}

/* ---------------------------------- entradas ---------------------------------- */

type DivProps = Omit<HTMLMotionProps<'div'>, 'initial' | 'animate'> & { delay?: number }

/** Aparece con un fundido. */
export function FadeIn({ delay = 0, transition, ...props }: DivProps) {
  const reduce = useReducedMotion()
  return (
    <m.div
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ...TRANSITION.base, delay, ...transition }}
      {...props}
    />
  )
}

/** Aparece con un fundido y un leve ascenso. */
export function SlideUp({ delay = 0, transition, ...props }: DivProps) {
  const reduce = useReducedMotion()
  return (
    <m.div
      initial={reduce ? false : { opacity: 0, y: RISE }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...TRANSITION.enter, delay, ...transition }}
      {...props}
    />
  )
}

const staggerItem: Variants = {
  hidden: { opacity: 0, y: RISE + 2 },
  show: { opacity: 1, y: 0, transition: TRANSITION.enter },
}

/**
 * Contenedor de una cascada corta: sus `StaggerItem` entran uno tras otro al montarse. Reemplaza al `div` de la
 * grilla (acepta `className`), así no agrega niveles al layout.
 */
export function Stagger({
  delay = 0,
  step = STAGGER,
  ...props
}: Omit<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'variants'> & { delay?: number; step?: number }) {
  const reduce = useReducedMotion()
  return (
    <m.div
      initial={reduce ? false : 'hidden'}
      animate="show"
      variants={{ show: { transition: { staggerChildren: step, delayChildren: delay } } }}
      {...props}
    />
  )
}

export function StaggerItem(props: Omit<HTMLMotionProps<'div'>, 'variants'>) {
  return <m.div variants={staggerItem} {...props} />
}

/* ---------------------------------- presencia ---------------------------------- */

/**
 * Muestra u oculta su contenido colapsando la altura (avisos, secciones opcionales). El margen hacia lo que sigue
 * va como relleno dentro del contenido, para que también colapse.
 */
export function Collapse({
  show,
  children,
  className,
  initial = false,
}: {
  show: boolean
  children: React.ReactNode
  className?: string
  /** Animar también si ya se muestra al montarse. */
  initial?: boolean
}) {
  return (
    <AnimatePresence initial={initial}>
      {show && (
        <m.div
          key="collapse"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={TRANSITION.collapse}
          className={cn('overflow-hidden', className)}
        >
          {children}
        </m.div>
      )}
    </AnimatePresence>
  )
}

/** Props de entrada/salida para elementos de lista dentro de `AnimatePresence` (filas que se agregan o quitan). */
export const listItemMotion = {
  initial: { opacity: 0, y: -RISE },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98, transition: { duration: DURATION.fast, ease: EASE_OUT } },
  transition: TRANSITION.base,
} as const

/* ---------------------------------- página ---------------------------------- */

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Transición de entrada entre pantallas: fundido + leve ascenso, solo al entrar (sin animación de salida que
 * duplique renders). Se dispara cuando cambia la ruta que se está mostrando, no con filtros ni paginación
 * (search params), y no vuelve a montar el contenido: anima el contenedor existente con la Web Animations API,
 * que al terminar no deja ningún `transform` residual (no afecta a hijos `fixed`/`sticky`).
 */
export function PageTransition({ children, className }: { children: React.ReactNode; className?: string }) {
  // La ruta efectivamente mostrada (no la que se está cargando): así no se anima la pantalla saliente.
  const pathname = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.pathname })
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !pathname || prefersReducedMotion() || typeof el.animate !== 'function') return
    const animation = el.animate(
      [
        { opacity: 0, transform: `translateY(${RISE}px)` },
        { opacity: 1, transform: 'none' },
      ],
      { duration: TRANSITION.enter.duration * 1000, easing: `cubic-bezier(${EASE_OUT.join(',')})` },
    )
    return () => animation.cancel()
  }, [pathname])
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  )
}

/* ---------------------------------- números ---------------------------------- */

/** Salida suave (quart-out), cercana a `EASE_OUT` y barata de evaluar en cada cuadro. */
const easeOutQuart = (t: number) => 1 - (1 - t) ** 4

interface AnimatedNumberProps {
  value: number
  /** Formato del número (p. ej. `formatMoney`). */
  format: (value: number) => string
  /** Valor desde el que cuenta al montarse (por defecto 0). `null` muestra el valor final sin contar. */
  from?: number | null
  /** Duración en segundos. */
  duration?: number
  className?: string
}

/**
 * Número que cuenta hasta su valor (montos de KPIs y totales). El ancho lo reserva el valor final invisible,
 * así la cuenta nunca mueve el layout; los lectores de pantalla leen solo el valor final. Escribe el texto
 * directo en el DOM en cada cuadro: no provoca renders de React.
 */
export function AnimatedNumber({
  value,
  format,
  from = 0,
  duration = DURATION.slower,
  className,
}: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const shown = useRef<number | null>(from)
  const formatRef = useRef(format)
  formatRef.current = format
  const reduce = useReducedMotion()
  const text = format(value)

  // biome-ignore lint/correctness/useExhaustiveDependencies: `text` cambia también si cambia el formato (moneda)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const start = shown.current
    if (reduce || start === null || start === value || !Number.isFinite(start)) {
      shown.current = value
      el.textContent = formatRef.current(value)
      return
    }
    el.textContent = formatRef.current(start)
    const began = performance.now()
    let frame = requestAnimationFrame(function tick(now) {
      const t = Math.min(1, (now - began) / (duration * 1000))
      const current = start + (value - start) * easeOutQuart(t)
      shown.current = t < 1 ? current : value
      el.textContent = formatRef.current(t < 1 ? current : value)
      if (t < 1) frame = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frame)
  }, [value, text, reduce, duration])

  return (
    <span className={cn('inline-grid tabular', className)}>
      <span aria-hidden className="invisible col-start-1 row-start-1">
        {text}
      </span>
      <span aria-hidden ref={ref} className="col-start-1 row-start-1">
        {text}
      </span>
      <span className="sr-only">{text}</span>
    </span>
  )
}
